import { useAtomValue, useSetAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { currentCloudDataAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { queriesEnabledAtom } from "../../../atoms/GlobalAtoms";
import API from "../../api/API";
import { fetchAllPermissions } from "../../../utils/UserUtils";
import { refreshToken } from "../../../context/UserContext/refreshToken";
import useLogout from "./useLogout";

const enabledRoutes = [
  "/clouds",
  "/create/cloud",
  "/account/profile",
  "/account/security",
  "/account/clouds",
];

/**
 * Handles fetching user, and fetching user permissions.
 *
 * 1 - Fetch User
 * 2 - Fetch User Permissions (cloud, workspace, and page permissions)
 *
 * useUser is disabled unless queriesEnabledAtom is true
 */
const useUser = (forceEnable = false) => {
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const setCurrentCloudData = useSetAtom(currentCloudDataAtom);
  const { logout } = useLogout();

  const user = useQuery({
    queryKey: ["user", tenantID],
    queryFn: async () => {
      try {
        const data = await API.getUser();

        const currentUser = data?.data?.data?.user;

        let permissions: {
          cPermissions: null | any[];
          wPermissions: null | any[];
          pPermissions: null | any[];
        } = {
          cPermissions: null,
          wPermissions: null,
          pPermissions: null,
        };

        // Only fetch permissions if the user has a tenant
        if (tenantID) {
          permissions = await fetchAllPermissions(setCurrentCloudData);
        }

        return { user: currentUser, permissions };
      } catch (e) {
        if (
          e &&
          typeof e === "object" &&
          "status" in e &&
          e.status === 401 &&
          enabledRoutes.includes(window.location.pathname)
        ) {
          refreshToken(undefined, () => logout({}));
        }
        console.error({ e });

        return null;
      }
    },
    enabled:
      queriesEnabled ||
      enabledRoutes.includes(window.location.pathname) ||
      forceEnable,
  });

  return {
    user: user.data?.user ?? null,
    userLoading: user.isLoading,
    cPermissions: user.data?.permissions?.cPermissions ?? null,
    wPermissions: user.data?.permissions?.wPermissions ?? null,
    pPermissions: user.data?.permissions?.pPermissions ?? null,
  };
};
export default useUser;
